/**
 * View helpers for the Report tab (docs/ui-design.md §3): pure functions
 * from a `Report` to what the UI shows. The report itself comes from
 * core/analysis.
 */
import type { Finding } from "../analysis";
import type { DataFlow } from "../canary";
import type { Severity } from "../events";
import { formatCsp, formatPermissionsPolicy, type Manifest } from "../manifest";
import { hostOf } from "../url/party";

/** Severities as the Report lists them, most severe first. */
export const REPORT_SEVERITIES: readonly Severity[] = ["critical", "suspicious", "notable", "info"];

/** How many findings there are of each severity (no overall score, ADR-0014). */
export function severityCounts(findings: readonly Finding[]): Record<Severity, number> {
  const counts: Record<Severity, number> = { critical: 0, suspicious: 0, notable: 0, info: 0 };
  for (const f of findings) counts[f.severity]++;
  return counts;
}

/** Findings from the tampering and evasion rules (ADR-0012) get their own section. */
export const isEvasionFinding = (f: Finding): boolean =>
  f.rule.startsWith("tamper.") || f.rule.startsWith("evasion.");

export function splitFindings(findings: readonly Finding[]): {
  findings: Finding[];
  evasion: Finding[];
} {
  return {
    findings: findings.filter((f) => !isEvasionFinding(f)),
    evasion: findings.filter(isEvasionFinding),
  };
}

/** A flow as `source ──via──▶ destination`. */
export function flowParts(flow: DataFlow): {
  source: string;
  via: string;
  destination: string;
  sink: string;
} {
  const via = [flow.encodings.join(", "), ...(flow.partial ? ["partial"] : [])]
    .filter((s) => s !== "")
    .join(" · ");
  const destination = flow.origin ? (hostOf(flow.origin) ?? flow.origin) : flow.sink;
  return { source: flow.source, via, destination, sink: flow.sink };
}

export interface ManifestBlock {
  id: "csp" | "permissions" | "deno";
  title: string;
  /** What the copy button copies. */
  text: string;
}

/** The manifest's non-empty policies, as copyable text. */
export function manifestBlocks(manifest: Manifest): ManifestBlock[] {
  const blocks: ManifestBlock[] = [];
  const csp = formatCsp(manifest.csp);
  if (csp !== "") {
    blocks.push({
      id: "csp",
      title: "Content-Security-Policy",
      text: `Content-Security-Policy: ${csp}`,
    });
  }
  if (manifest.permissions.length > 0) {
    blocks.push({
      id: "permissions",
      title: "Permissions-Policy",
      text: `Permissions-Policy: ${formatPermissionsPolicy(manifest.permissions)}`,
    });
  }
  if (manifest.flags.length > 0) {
    blocks.push({ id: "deno", title: "Deno-style flags", text: manifest.flags.join(" ") });
  }
  return blocks;
}
